import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import useGeolocation from './useGeolocation';
import { resolveUserLocationContext } from '../utils/userLocationDisplay';

/**
 * Hook to manage user location - detects location and updates user profile if missing
 * Also provides location data for filtering services
 */
const useUserLocation = () => {
  const { user, updateUser, isAuthenticated } = useAuth();
  const {
    location: geoLocation,
    address,
    city: geoCity,
    state: geoState,
    country: geoCountry,
    getCurrentLocation,
    loading: geoLoading,
    error: geoError,
  } = useGeolocation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [locationUpdated, setLocationUpdated] = useState(false);

  const needsLocationUpdate = user && (
    !user.latitude ||
    !user.longitude ||
    !user.country ||
    !user.ip_address
  );

  const updateUserLocation = async (locationData, resolved) => {
    // NEVER automatically update vendor's registered location in the background
    if (!isAuthenticated || !user || isUpdating || user?.user_type === 'vendor') return;

    setIsUpdating(true);
    try {
      const updateData = {
        country: resolved?.country || geoCountry || user?.country || null,
        city: resolved?.city || user?.city || null,
        state: resolved?.state || user?.state || null,
        latitude: locationData?.latitude || null,
        longitude: locationData?.longitude || null,
      };

      try {
        await authService.updateProfile(updateData);
        updateUser({ ...user, ...updateData });
        setLocationUpdated(true);
      } catch (error) {
        console.error('Error updating user location:', error);
        updateUser({ ...user, ...updateData });
      }
    } catch (error) {
      console.error('Error updating user location:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    getCurrentLocation({ preferIp: true });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !geoLoading && !isUpdating) {
        getCurrentLocation({ preferIp: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const refreshInterval = setInterval(() => {
      if (!geoLoading && !isUpdating) {
        getCurrentLocation({ preferIp: true });
      }
    }, 2 * 60 * 1000);

    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!geoLocation || isUpdating || user?.user_type === 'vendor') return;

    const resolved = resolveUserLocationContext({
      geoCity,
      geoState,
      geoCountry,
      address,
      latitude: geoLocation.latitude,
      longitude: geoLocation.longitude,
      user,
    });

    if (!resolved.country && !resolved.city) return;

    const hasStoredLocation = user?.latitude && user?.longitude;
    let shouldUpdate = false;

    if (!hasStoredLocation || needsLocationUpdate) {
      shouldUpdate = !locationUpdated;
    } else if (user?.id) {
      const storedLat = parseFloat(user.latitude);
      const storedLng = parseFloat(user.longitude);
      const newLat = parseFloat(geoLocation.latitude);
      const newLng = parseFloat(geoLocation.longitude);

      const R = 6371;
      const dLat = ((newLat - storedLat) * Math.PI) / 180;
      const dLng = ((newLng - storedLng) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((storedLat * Math.PI) / 180) *
          Math.cos((newLat * Math.PI) / 180) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2);
      const distance = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      const countryChanged =
        resolved.country &&
        user?.country &&
        resolved.country.toLowerCase() !== user.country.toLowerCase();

      const cityChanged =
        resolved.city &&
        user?.city &&
        resolved.city.toLowerCase() !== user.city.toLowerCase();

      if (distance > 10 || countryChanged || cityChanged) {
        shouldUpdate = true;
        setLocationUpdated(false);
      }
    }

    if (shouldUpdate && isAuthenticated) {
      updateUserLocation(geoLocation, resolved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    geoLocation?.latitude,
    geoLocation?.longitude,
    geoCity,
    geoState,
    geoCountry,
    address,
    isUpdating,
    user?.city,
    user?.country,
  ]);

  const userLocation = useMemo(() => {
    const lat =
      geoLocation?.latitude != null
        ? parseFloat(geoLocation.latitude)
        : user?.latitude
          ? parseFloat(user.latitude)
          : null;
    const lng =
      geoLocation?.longitude != null
        ? parseFloat(geoLocation.longitude)
        : user?.longitude
          ? parseFloat(user.longitude)
          : null;

    const resolved = resolveUserLocationContext({
      geoCity,
      geoState,
      geoCountry,
      address,
      latitude: lat,
      longitude: lng,
      user,
    });

    if (!resolved.display && !resolved.country && !resolved.city) {
      return null;
    }

    return {
      latitude: lat,
      longitude: lng,
      city: resolved.city,
      state: resolved.state,
      country: resolved.country,
      address: resolved.display,
      display: resolved.display,
      hasCity: resolved.hasCity,
    };
  }, [
    geoLocation?.latitude,
    geoLocation?.longitude,
    geoCity,
    geoState,
    geoCountry,
    address,
    user,
  ]);

  return {
    userLocation,
    needsUpdate: needsLocationUpdate,
    isUpdating,
    loading: geoLoading || isUpdating,
    error: geoError,
    updateLocation: () => {
      getCurrentLocation({ preferIp: true });
    },
  };
};

export default useUserLocation;
